
import { Enums, Helpers } from '@bode-canada/core';
import ExternalLink from '@/ui-kit/components/external-link';
import { ExternalLinks } from '@/utils/external-navigation/constants';
import { ExternalLinkProperty } from '@/ui-kit/components/external-link/enums';
import { IconType } from '@/ui-kit/components/icon/enums';
import NumberFormat from '@/ui-kit/helpers/NumberFormatHelper';
import { QueryParams } from '@/constants/QueryParams';
import Typography from '@/ui-kit/components/typography';
import { TypographyProperty, TypographyType } from '@/ui-kit/components/typography/enums';
import Divider from '@/ui-kit/components/divider';
import Gutter from '@/ui-kit/components/gutter';
import { GutterProperty } from '@/ui-kit/components/gutter/enums';
import {
  StylePositionType,
  StyleSizeType,
  StyleFontWeightType,
  StyleColorSchemeType,
} from '@/ui-kit/styles/enums';
import LayoutGridContainer from '@/ui-kit/components/layout-grid/container';
import LayoutGridItem from '@/ui-kit/components/layout-grid/item';
import { LayoutItemProperty, LayoutItemColumn } from '@/ui-kit/components/layout-grid/item/enums';
import { LayoutContainerProperty } from '@/ui-kit/components/layout-grid/container/enums';
import MarketDataSummaryTableCard
  from '@/components/features/market-data/components/summary-table/MarketDataSummaryTableCard';

const SEARCH_LISTINGS_FILTERS = [
  'minPrice',
  'maxPrice',
  'yearBuilt',
];

export default {
  name: 'MarketDataSummaryTable',
  components: {
    Typography,
    Divider,
    Gutter,
    ExternalLink,
    LayoutGridContainer,
    LayoutGridItem,
    MarketDataSummaryTableCard,
  },
  props: {
    summary: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      TypographyProperty,
      TypographyType,
      StyleFontWeightType,
      GutterProperty,
      StylePositionType,
      StyleSizeType,
      StyleColorSchemeType,
      LayoutItemProperty,
      LayoutItemColumn,
      LayoutContainerProperty,
      ExternalLinks,
      ExternalLinkProperty,
    };
  },
  computed: {
    activeListingsData() {
      const activeListingRows = this.summary[Enums.ListingStatusEnum.ACTIVE];

      const daysOnMarket = activeListingRows?.daysOnMarket
        ? NumberFormat.format(Math.round(activeListingRows.daysOnMarket))
        : 'Insufficient data available';
      const averageListingPrice = activeListingRows?.price
        ? NumberFormat.formatCurrency(Math.round(activeListingRows.price))
        : 'Insufficient data available';
      const averageListPriceSqft = activeListingRows?.pricePerSqft
        ? NumberFormat.formatCurrency(Math.round(activeListingRows.pricePerSqft))
        : 'Insufficient data available';

      return {
        title: 'Active Listings',
        rows: [
          {
            icon: IconType.Homes,
            title: 'Number Of Listings',
            value: activeListingRows?.listingsAmount
              ? NumberFormat.format(activeListingRows.listingsAmount)
              : null,
          },
          {
            icon: IconType.CalendarEmpty,
            title: 'Days On Market',
            value: activeListingRows ? daysOnMarket : null,
          },
          {
            icon: IconType.HomePrice,
            title: 'Average List Price',
            value: activeListingRows ? averageListingPrice : null,
          },
          {
            icon: IconType.HomeSize,
            title: 'Average List Price / Sqft',
            value: activeListingRows ? averageListPriceSqft : null,
          },
        ],
        buttonText: 'See Active Listings',
      };
    },

    soldListingsData() {
      const soldListingRows = this.summary[Enums.ListingStatusEnum.SOLD];

      const daysOnMarket = soldListingRows?.daysOnMarket
        ? NumberFormat.format(soldListingRows.daysOnMarket)
        : 'Insufficient data available';
      const averageListingPrice = soldListingRows?.price
        ? NumberFormat.formatCurrency(soldListingRows.price)
        : 'Insufficient data available';
      const averageListPriceSqft = soldListingRows?.pricePerSqft
        ? NumberFormat.formatCurrency(soldListingRows.pricePerSqft)
        : 'Insufficient data available';
      const ratio = soldListingRows?.soldRatio
        ? NumberFormat.formatPercentage(soldListingRows.soldRatio)
        : 'Insufficient data available';

      return {
        title: 'Sold Listings',
        rows: [
          {
            icon: IconType.Homes,
            title: 'Number Of Listings',
            value: soldListingRows?.listingsAmount
              ? NumberFormat.format(soldListingRows.listingsAmount)
              : null,
          },
          {
            icon: IconType.CalendarEmpty,
            title: 'Days On Market',
            value: soldListingRows ? daysOnMarket : null,
          },
          {
            icon: IconType.HomePrice,
            title: 'Average Sold Price',
            value: soldListingRows ? averageListingPrice : null,
          },
          {
            icon: IconType.HomeSize,
            title: 'Average Sold Price / Sqft',
            value: soldListingRows ? averageListPriceSqft : null,
          },
          {
            icon: IconType.Percent,
            title: 'Sold Price / List Price',
            value: soldListingRows ? ratio : null,
          },
        ],
        buttonText: 'See Sold Listings',
      };
    },
  },
  methods: {
    async onClickSeeActiveListings() {
      this.$track('Market Data_See Active Listings button click');
      // Pass filters params to search listings page
      // it should be ?address=x&minPrice=x&maxPrice=x&propertyType=x,x,x&yearBuilt=x,x,x
      const filtersQuerySearch = await this.appenedFiltersToUrl();
      this.$router.push(`/search?${filtersQuerySearch}`);
    },

    onClickSeeSoldDataButton() {
      this.$track('Market Data_See Sold Data button click');
      const { query } = this.$route;
      const searchParams = new URLSearchParams({
        ...Helpers.ObjectHelper.removeEmptyProperties({
          [QueryParams.Province]: query[QueryParams.Province],
          [QueryParams.City]: query[QueryParams.City],
          [QueryParams.TransactionDate]: query[QueryParams.TransactionDate],
          [QueryParams.Community]: query[QueryParams.Community]?.split(',')[0],
        }),
      });
      this.$router.push(`/sold-data?${searchParams.toString()}`);
    },

    appenedFiltersToUrl() {
      const { community, city } = this.$route.query;

      let querySearch = '';
      // Handle address filters which should be community value if exists
      // or city if no community value
      querySearch += `searchAddress=${community ? community.split(',')[0] : city}&`;

      // Append other filters to url
      const filtersObject = this.$route.query;
      if (filtersObject) {
        Object.keys(filtersObject).forEach((property) => {
          SEARCH_LISTINGS_FILTERS.forEach((item) => {
            if (item === property && filtersObject[property]) {
              // replace minPrice and maxPrice with their values not indexs
              if (property === 'minPrice') filtersObject.minPrice = this.priceRange[filtersObject[property]];
              else if (property === 'maxPrice') filtersObject.maxPrice = this.priceRange[filtersObject[property]];

              querySearch += `search${property.slice(0, 1).toUpperCase() + property.slice(1)}=${filtersObject[property]}&`;
            }
          });
        });
      }

      return querySearch;
    },
  },
};
